import { useCallback, useMemo } from 'react';
import * as Yup from 'yup';
import { Formik, Form, FormikHelpers } from 'formik';
import { createUseStyles } from 'react-jss';

import {
  BillingInformation,
  Consent,
  ConsentName,
  Invoice,
  useAcceptConsent,
  useCurrentOrganizationId,
  useGenerateInvoiceForOffer,
  useUpdateBillingAddressDetails,
} from '@app/api';
import { ApiValidationFailedException } from 'store/modules/exceptions';
import { Theme } from '@app/theme';

import { InformationBox, SubmitButton, useToast } from '@app/core';
import { Input, Checkbox } from '@app/form';
import {
  BillingInformationFormFields,
  billingValidationSchema,
  ConsentCheckbox,
  useBillingInfoInitialValues,
} from 'components';
import { useTranslation } from 'react-i18next';

interface Props {
  billingInformation: BillingInformation;
  onSuccess?: (invoice: Invoice) => any;
  onShouldApplyVatChange?: (val: boolean) => any;
  offerId: string;
}

const useStyles = createUseStyles((theme: Theme) => ({
  buttonsLine: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    marginTop: theme.marginBase * 4,
  },
  subTitle: {
    ...theme.fonts.h4,
    marginBottom: theme.marginBase,
    marginTop: theme.marginBase * 4,
  },
  oneLineOptions: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: theme.marginBase * 2,
    marginBottom: theme.marginBase * 2,
    marginTop: theme.marginBase * 2,
  },
  paymentMethod: {
    backgroundColor: theme.colors.grayBackground,
    padding: theme.marginBase * 2,
    paddingBottom: 0,
    borderRadius: theme.borderRadius.std,

    opacity: 0,
    maxHeight: 0,

    transition: 'opacity 0.2s ease-in-out, max-height 0.1s ease-in-out',
  },
  box: {
    marginTop: theme.marginBase * 2,
  },
}));

interface Values {
  billingInformation: BillingInformation;
  consents: Consent[];
  purchaseOrder?: string;
}

export const PaymentForm = ({
  billingInformation,
  onSuccess,
  onShouldApplyVatChange,
  offerId,
}: Props) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const toast = useToast();
  const billingInfoInitial = useBillingInfoInitialValues(billingInformation);
  const { mutateAsync: updateBillingInformation } =
    useUpdateBillingAddressDetails(billingInformation.id);

  const organizationId = useCurrentOrganizationId();
  const { mutateAsync: generateInvoice } = useGenerateInvoiceForOffer(offerId);
  const { mutateAsync: acceptConsent } = useAcceptConsent();

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        billingInformation: billingValidationSchema,
        consents: Yup.array().length(
          1,
          t('billing.paymentForm.consentRequired'),
        ),
        purchaseOrder: Yup.string().nullable().max(140),
      }),
    [t],
  );

  const onSubmit = useCallback(
    async (values: Values, { setStatus }: FormikHelpers<Values>) => {
      if (!organizationId) {
        return;
      }

      try {
        await acceptConsent(values.consents[0]);
        await updateBillingInformation(values.billingInformation);
        const invoice = await generateInvoice({
          organizationId,
          purchaseOrder: values.purchaseOrder,
        });
        if (onSuccess) {
          onSuccess(invoice);
        }
        toast.saved();
      } catch (e) {
        toast.exception(e);
        if (e instanceof ApiValidationFailedException) {
          const isBillingError = e.config?.url?.includes('address-details');
          setStatus(
            isBillingError
              ? Object.fromEntries(
                  Object.entries(e.getFormikErrors()).map(([key, value]) => [
                    `billingInformation.${key}`,
                    value,
                  ]),
                )
              : e.getFormikErrors(),
          );
        }
      }
    },
    [
      toast,
      updateBillingInformation,
      onSuccess,
      generateInvoice,
      acceptConsent,
      organizationId,
    ],
  );

  const initialValues: Values = useMemo(
    () => ({
      billingInformation: billingInfoInitial,
      consents: [],
    }),
    [billingInfoInitial],
  );

  if (!organizationId) {
    return null;
  }
  return (
    <Formik
      initialValues={initialValues}
      onSubmit={onSubmit}
      validationSchema={validationSchema}
    >
      <Form>
        <InformationBox
          text={t('billing.paymentForm.paymentMethodInfo')}
          className={classes.box}
        />

        <h2 className={classes.subTitle}>
          {t('billing.paymentForm.billingInfo')}
        </h2>
        <BillingInformationFormFields
          onShouldApplyVatChange={onShouldApplyVatChange}
          initialValues={initialValues.billingInformation}
          name="billingInformation"
        />

        <Input
          name="purchaseOrder"
          label={t('billing.paymentForm.purchaseOrder')}
        />

        <ConsentCheckbox
          consent={ConsentName.CLIENT_STUDY_TERMS}
          name="consents"
          alwaysShow
        />

        <div className={classes.buttonsLine}>
          <SubmitButton text={t('billing.paymentForm.confirm')} />
        </div>
      </Form>
    </Formik>
  );
};
